import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ Error Boundary Caught:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });

    // Log to console for mobile debugging
    try {
      const errorData = {
        message: error?.message || 'Unknown error',
        stack: error?.stack || 'No stack trace',
        componentStack: errorInfo?.componentStack || 'No component stack',
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent
      };
      
      console.log('📱 MOBILE ERROR DEBUG:', JSON.stringify(errorData, null, 2));
      
      // Try to save to localStorage for debugging
      localStorage.setItem('lastError', JSON.stringify(errorData));
    } catch (e) {
      console.error('Could not log error:', e);
    }
  }

  handleReload = () => {
    // Clear service worker cache
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }
    
    // Clear localStorage caches
    const keysToKeep = ['firebase:authUser', 'firebase:host'];
    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      if (!keysToKeep.some(keep => key.includes(keep))) {
        localStorage.removeItem(key);
      }
    });
    
    // Force hard reload
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      return (
        <View style={styles.container}>
          <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
            <Text style={styles.title}>😔 Oops! Something went wrong</Text>
            
            <Text style={styles.message}>
              The app encountered an error. This has been logged for debugging.
            </Text>

            <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.button} onPress={this.handleReload}>
              <Text style={styles.buttonText}>🔄 Clear Cache & Reload</Text>
            </TouchableOpacity>

            <TouchableOpacity dataSet={{ hover: "lift" }} 
              style={[styles.button, styles.buttonSecondary]} 
              onPress={() => window.location.href = '/'}
            >
              <Text style={styles.buttonText}>🏠 Go to Home</Text>
            </TouchableOpacity>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <View style={styles.errorDetails}>
                <Text style={styles.errorTitle}>Error Details (Dev Only):</Text>
                <Text style={styles.errorText}>
                  {this.state.error.toString()}
                </Text>
                {this.state.errorInfo && (
                  <Text style={styles.stackText}>
                    {this.state.errorInfo.componentStack}
                  </Text>
                )}
              </View>
            )}

            <Text style={styles.footer}>
              If this problem persists, try using a desktop browser or contact support.
            </Text>
          </ScrollView>
        </View>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F6F8F7',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2E3A38',
    marginBottom: 16,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    color: '#5C6B68',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 24,
  },
  button: {
    backgroundColor: '#0E7C6B',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginBottom: 12,
    minWidth: 250,
  },
  buttonSecondary: {
    backgroundColor: '#0E7C6B',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  errorDetails: {
    marginTop: 32,
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#C4453D',
    width: '100%',
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#C4453D',
    marginBottom: 8,
  },
  errorText: {
    fontSize: 12,
    color: '#2E3A38',
    fontFamily: 'monospace',
    marginBottom: 8,
  },
  stackText: {
    fontSize: 11,
    color: '#5C6B68',
    fontFamily: 'monospace',
  },
  footer: {
    marginTop: 32,
    fontSize: 12,
    color: '#8A9794',
    textAlign: 'center',
  },
});

export default ErrorBoundary;

